import { MinusIcon, PlusIcon, ShoppingBagIcon, XIcon } from "lucide-react"
import Link from "next/link"
import type { ComponentProps } from "react"
import { cn } from "@/utilities"

const brl = new Intl.NumberFormat("pt-BR", {
  currency: "BRL",
  style: "currency",
})

const toCurrency = (value: number): string => brl.format(value)

interface CartTableItem {
  id: string
  name: string
  slug: string
  price: number
  quantity: number
  store: string
}

interface CartItemsTableProps extends ComponentProps<"div"> {
  items: CartTableItem[]
  onRemoveItem: (id: string) => void
  onUpdateQuantity: (id: string, nextQuantity: number) => void
}

export const CartItemsTable = ({
  className,
  items,
  onRemoveItem,
  onUpdateQuantity,
  ...props
}: CartItemsTableProps) => {
  return (
    <div className={cn("overflow-x-auto", className)} {...props}>
      <table className='w-full min-w-190 border-separate border-spacing-0'>
        <thead>
          <tr className='bg-muted text-left font-semibold text-[11px] uppercase tracking-[0.12em]'>
            <th className='px-4 py-3 text-muted-foreground'>Produto</th>
            <th className='px-4 py-3 text-muted-foreground'>Preco</th>
            <th className='px-4 py-3 text-muted-foreground'>Quantidade</th>
            <th className='px-4 py-3 text-muted-foreground'>Total</th>
            <th className='px-4 py-3 text-muted-foreground' />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td
                className='px-4 py-8 text-center text-muted-foreground'
                colSpan={5}
              >
                Seu carrinho esta vazio.
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <tr className='align-top' key={item.id}>
                <td className='px-4 py-6'>
                  <div className='flex items-start gap-4'>
                    <div className='flex h-18 w-18 shrink-0 items-center justify-center rounded-xl border bg-white shadow-xs'>
                      <ShoppingBagIcon aria-hidden='true' size={28} />
                    </div>
                    <div className='space-y-2'>
                      <Link
                        className='font-medium text-link leading-6 hover:underline'
                        href={`/produtos/${item.slug}`}
                      >
                        {item.name}
                      </Link>
                      <p className='text-muted-foreground text-sm'>
                        Lojista:{" "}
                        <span className='text-foreground'>{item.store}</span>
                      </p>
                    </div>
                  </div>
                </td>
                <td className='px-4 py-6 font-medium text-foreground/90'>
                  {toCurrency(item.price)}
                </td>
                <td className='px-4 py-6'>
                  <div className='flex w-fit items-center rounded-md border bg-white'>
                    <button
                      aria-label='Diminuir quantidade'
                      className='px-2 py-1 transition-colors hover:bg-muted'
                      onClick={() => {
                        onUpdateQuantity(item.id, item.quantity - 1)
                      }}
                      type='button'
                    >
                      <MinusIcon size={14} />
                    </button>
                    <span className='min-w-8 border-x px-2 py-1 text-center text-sm'>
                      {item.quantity}
                    </span>
                    <button
                      aria-label='Aumentar quantidade'
                      className='px-2 py-1 transition-colors hover:bg-muted'
                      onClick={() => {
                        onUpdateQuantity(item.id, item.quantity + 1)
                      }}
                      type='button'
                    >
                      <PlusIcon size={14} />
                    </button>
                  </div>
                </td>
                <td className='px-4 py-6 font-semibold'>
                  {toCurrency(item.price * item.quantity)}
                </td>
                <td className='px-4 py-6'>
                  <button
                    aria-label='Remover item'
                    className='text-muted-foreground hover:text-foreground'
                    onClick={() => {
                      onRemoveItem(item.id)
                    }}
                    type='button'
                  >
                    <XIcon size={16} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
